import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import ICompAddress from '@/models/CompAddress';
import IFeedData from '@/models/comp/IFeedData';
import IPublicRecord from '@/models/public-records/IPublicRecord';
import PublicRecordsRequest from '@/models/requests/PublicRecordsRequest';
import WebEnvConfig from '@/models/WebEnvConfig';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import ServiceHelperFactory from './ServiceHelperFactory';
import BaseApiService from '../BaseApiService';
import IServiceHelper from '../IServiceHelper';

// Internal use only enums and structures related to building the request.
enum PublicRecordPropertyTypes {
    Subject = 1,
    Comparable = 2,
}

interface IGetSDPRequestParams {
    address: string;
    city: string;
    state: string;
    zip: string;
    form: string;
    propertyType: PublicRecordPropertyTypes;
}

const productId = `3`; // This is QuickSource

/**
 * Service for getting public records using the QS API endpoints. Will get from either public data (v1.0/datadiscrepancy) or
 * smart data platform (v5/public-records)
 */
export default class PublicRecordsService extends BaseApiService {
    constructor(webEnvConfig: WebEnvConfig) {
        super(webEnvConfig.urls?.quickSourceApi ?? '');
    }

    getServiceHelper(): IServiceHelper {
        return ServiceHelperFactory.getHelper();
    }

    /**
     * Get a feed data from the QS data discrepancy endpoint (public data) using the given request.
     * @param prRequest
     * @returns a feed data or null on not found or error
     */
    async getFromPublicData(prRequest: PublicRecordsRequest): Promise<IFeedData | null> {
        this.addContentTypeJsonHeader();
        this.addAcceptJsonHeader();
        //this.setLogRequest(true);

        try {
            const response = await this.axiosInstance.post('v1.0/DataDiscrepancy/PublicRecord', prRequest);
            if (response.status === 200) {
                const result: IFeedData = response.data;
                return result;
            }
        } catch (error) {
            const errorDescription = this.getErrorDescription(error);
            const addressDescription = CompAddressUtils.getDescription(prRequest.address);
            console.log(`PublicRecordService.getFromPublicData(), Error[${errorDescription}] on address[${addressDescription}], isSubject[${prRequest.isSubject}], formCode[${prRequest.formCode}]`);
        }

        return null;
    }

    /**
     * Get a public record from the QS public records endpoint (smart data platform) using the given parameters.
     * Required values within the given address are address1, city, state and zip code.
     *
     * @param address
     * @param isSubject
     * @param formCode
     * @returns a public record or null on error
     */
    async getFromSmartDataPlatform(address: ICompAddress, isSubject: boolean, formCode: string): Promise<IPublicRecord | null> {
        this.addHeaders({
            'alm-calling-product-name': `${productId}`,
        });
        const p2id = theLocalStorageUtils.get(LocalStorageKeys.p2id);
        if (p2id) {
            this.addHeaders({
                'alm-p2id': `${p2id}`,
            });
        }
        // this.setLogRequest(true);

        const requestParams: IGetSDPRequestParams = {
            address: address.address1,
            city: address.city ?? '',
            state: address.state ?? '',
            zip: address.zipCode ?? '',
            form: formCode ?? '',
            propertyType: isSubject ? PublicRecordPropertyTypes.Subject : PublicRecordPropertyTypes.Comparable,
        };

        // Any unit number should be appended to address1 for the QS SDP endpoint
        if (address.unitNumber) {
            requestParams.address += ` ${address.unitNumber}`;
        }

        try {
            const query = this.buildQuery(requestParams);
            const response = await this.axiosInstance.get(`v5/public-records/comp${query}`);
            if (response.status === 200) {
                const result: IPublicRecord = response.data;
                return result;
            }
        } catch (error) {
            const errorDescription = this.getErrorDescription(error);
            const addressDescription = CompAddressUtils.getDescription(address);
            console.log(`PublicRecordService.getFromSmartDataPlatform(), Error[${errorDescription}] on address[${addressDescription}], isSubject[${isSubject}], formCode[${formCode}]`);
        }

        return null;
    }
}
